import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  Search,
  Filter,
  Eye,
  Users,
  Download,
  Upload,
  FileSpreadsheet
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from './ui/table';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import GridPagination from './GridPagination';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmployeeList = () => {
  const token = localStorage.getItem('token');
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, employee: null });

  // Estados de paginação
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page'), 10) || 1);
  const [searchDebounce, setSearchDebounce] = useState(searchParams.get('search') || '');

  // Import/Export de planilha
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);

  // Debounce para busca
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounce(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Resetar para página 1 apenas quando a busca efetivamente mudar (não na montagem,
  // que já traz a página vinda da URL). Comparar com o valor anterior em vez de um
  // simples "é a primeira renderização?" evita reset espúrio causado pela dupla
  // invocação de efeitos do React.StrictMode em desenvolvimento.
  const prevSearchDebounce = useRef(searchDebounce);
  useEffect(() => {
    if (prevSearchDebounce.current !== searchDebounce) {
      prevSearchDebounce.current = searchDebounce;
      setCurrentPage(1);
    }
  }, [searchDebounce]);

  useEffect(() => {
    fetchEmployees();
  }, [currentPage, searchDebounce]);

  // Mantém página/busca na URL para preservar o estado ao voltar da edição de um funcionário
  useEffect(() => {
    const params = {};
    if (currentPage > 1) params.page = String(currentPage);
    if (searchDebounce) params.search = searchDebounce;
    setSearchParams(params, { replace: true });
  }, [currentPage, searchDebounce]);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      
      if (!token) {
        toast.error('Sessão expirada. Faça login novamente.');
        setLoading(false);
        return;
      }
      
      // Usar endpoint paginado
      const params = new URLSearchParams({
        page: currentPage,
        limit: 10
      });
      
      if (searchDebounce) {
        params.append('search', searchDebounce);
      }
      
      const response = await axios.get(`${API}/employees-paged?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // Verificar formato de resposta paginada
      if (response.data && response.data.data && response.data.pagination) {
        setEmployees(response.data.data);
        setPagination(response.data.pagination);
      } else {
        // Fallback para resposta antiga (array simples)
        const employeesData = Array.isArray(response.data) ? response.data : [];
        setEmployees(employeesData);
        setPagination(null);
      }
      
    } catch (error) {
      console.error('Erro ao carregar funcionários:', error);
      
      if (error.response?.status === 401) {
        toast.error('Não autorizado. Faça login novamente.');
      } else {
        toast.error('Erro ao carregar lista de funcionários.');
      }
      
      setEmployees([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleDelete = async (employee) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/employees/${employee.id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      toast.success('Funcionário removido com sucesso!');
      fetchEmployees();
      setDeleteDialog({ open: false, employee: null });
    } catch (error) {
      console.error('Erro ao remover funcionário:', error);
      toast.error('Erro ao remover funcionário');
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/employees/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'funcionarios.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erro ao exportar funcionários');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/employees/import-template`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'modelo_importacao_funcionarios.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erro ao baixar modelo');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/employees/import`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const { criados, ignorados, total_erros, erros, total_avisos, avisos } = response.data;

      toast.success(`Importação concluída: ${criados} criados, ${ignorados} já existentes (ignorados)`);
      if (total_erros > 0) {
        toast.error(`${total_erros} linha(s) com erro. Primeira: ${erros[0]}`);
        console.warn('Erros de importação:', erros);
      }
      if (total_avisos > 0) {
        toast.message(`${total_avisos} linha(s) importadas com algum vínculo não encontrado (ex.: empresa/escolaridade). Veja o console para detalhes.`);
        console.warn('Avisos de importação:', avisos);
      }

      fetchEmployees(); // recarregar lista
    } catch (error) {
      const detail = error.response?.data?.detail || 'Erro ao importar planilha';
      toast.error(detail);
    } finally {
      setImporting(false);
      e.target.value = ''; // permite reimportar o mesmo arquivo
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('pt-BR');
    } catch {
      return dateString;
    }
  };

  const getStatusBadgeVariant = (status) => {
    switch (status?.toLowerCase()) {
      case 'ativo':
        return 'default';
      case 'inativo':
        return 'secondary';
      case 'demitido':
        return 'destructive';
      case 'afastado':
        return 'outline';
      default:
        return 'secondary';
    }
  };

  const getRegimeBadgeVariant = (regime) => {
    return regime === 'CLT' ? 'default' : 'outline';
  };

  // Componente para exibir foto do colaborador
  const EmployeeAvatar = ({ employee }) => {
    if (employee.foto) {
      return (
        <img
          src={employee.foto}
          alt={employee.nome}
          className="w-10 h-10 rounded-full object-cover mr-3"
          onError={(e) => {
            // Se a imagem falhar ao carregar, mostrar iniciais
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
        />
      );
    }
    // Fallback: mostrar iniciais se não tiver foto
    const initials = employee.nome
      .split(' ')
      .map(n => n[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
    
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-yellow-500',
      'bg-red-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500'
    ];
    
    const colorIndex = employee.nome.charCodeAt(0) % colors.length;
    
    return (
      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm mr-3 ${colors[colorIndex]}`}>
        {initials}
      </div>
    );
  };

  // Não precisa mais de filtro local - busca é feita no backend
  const displayEmployees = employees;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="loading-spinner w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Funcionários</h1>
          <p className="text-slate-600 mt-1">
            Gerencie a lista de funcionários da empresa
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="file"
            accept=".xlsx"
            ref={fileInputRef}
            onChange={handleImport}
            className="hidden"
          />
          <Button
            variant="outline"
            onClick={handleDownloadTemplate}
            className="flex items-center space-x-2"
          >
            <FileSpreadsheet className="h-4 w-4" />
            <span>Modelo</span>
          </Button>
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            className="flex items-center space-x-2"
          >
            <Upload className="h-4 w-4" />
            <span>{importing ? 'Importando...' : 'Importar'}</span>
          </Button>
          <Button
            variant="outline"
            onClick={handleExport}
            className="flex items-center space-x-2"
          >
            <Download className="h-4 w-4" />
            <span>Exportar</span>
          </Button>
          <Button
            onClick={() => navigate('/funcionarios/detalhado/novo', { state: { from: `${location.pathname}${location.search}` } })}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Novo Funcionário</span>
          </Button>
        </div>
      </div>

      {/* Search and filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
              <Input
                placeholder="Buscar por nome, empresa ou cargo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Badge variant="outline" className="flex items-center space-x-2 self-start sm:self-auto">
              <Users className="h-3 w-3" />
              <span>{pagination ? pagination.total : displayEmployees.length} funcionários</span>
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Employee table */}
      <Card className="table-container">
        <CardHeader>
          <CardTitle>Lista de Funcionários</CardTitle>
          <CardDescription>
            Visualize e gerencie todos os funcionários cadastrados
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Empresa</TableHead>
                  <TableHead>Cargo</TableHead>
                  <TableHead>Regime</TableHead>
                  <TableHead>Situação</TableHead>
                  <TableHead>Admissão</TableHead>
                  <TableHead className="text-center">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayEmployees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                      {searchTerm ? 'Nenhum funcionário encontrado com os filtros aplicados' : 'Nenhum funcionário cadastrado'}
                    </TableCell>
                  </TableRow>
                ) : (
                  displayEmployees.map((employee) => (
                    <TableRow key={employee.id} className="table-row">
                      <TableCell className="font-medium">
                        <div className="flex items-center">
                          <EmployeeAvatar employee={employee} />
                          <span>{employee.nome}</span>
                        </div>
                      </TableCell>
                      <TableCell>{employee.empresa}</TableCell>
                      <TableCell>{employee.cargo || '-'}</TableCell>
                      <TableCell>
                        <Badge variant={getRegimeBadgeVariant(employee.regime)}>
                          {employee.regime}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusBadgeVariant(employee.situacao)}>
                          {employee.situacao || 'N/A'}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDate(employee.admissao)}</TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/funcionarios/detalhado/editar/${employee.id}`, { state: { from: `${location.pathname}${location.search}` } })}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteDialog({ open: true, employee })}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          
          {/* Paginação */}
          {pagination && (
            <GridPagination 
              pagination={pagination}
              onPageChange={handlePageChange}
              loading={loading}
            />
          )}
        </CardContent>
      </Card>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, employee: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja remover o funcionário <strong>{deleteDialog.employee?.nome}</strong>?
              Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialog({ open: false, employee: null })}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleDelete(deleteDialog.employee)}
              className="btn-danger"
            >
              Remover
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeeList;
